import { HttpRequest } from "../services/httpRequest";
import { agruparMovimientosPorConcepto } from "./movimientos.utils";

/**
 * Resuelve endpoint según versionTarifas, construye costsFeature
 * y calcula deudaHoy.
 *
 * @returns {{ costsFeature, deudaHoy, infoDeudaBackend } | null}
 */
/** @returns {Promise<{costsFeature: Array, deudaHoy: number, infoDeudaBackend: object} | null>} */
export async function resolverDeudaHoy(credit, infoCupo) {
  const versionTarifas = credit?.versionTarifas;

  let responseData;

  switch (versionTarifas) {
    case "1":
      responseData = await HttpRequest.calcularDeuda({ credit, infoCupo });
      break;
    case "2":
      responseData = await HttpRequest.calcularDeudaV2({ credit, infoCupo });
      break;
    default:
      responseData = await HttpRequest.calcularDeuda({ credit, infoCupo });
      break;
  }

  const infoDeudaBackend = responseData?.message?.deuda;

  if (!infoDeudaBackend?.hasSaldoCapital) return null;

  const costsFeature = [...(infoDeudaBackend?.costsFeature ?? [])];

  if (infoDeudaBackend?.firmaElectronicaNet) {
    costsFeature.push({
      item: "DCTO Abono Firma",
      value: infoDeudaBackend.firmaElectronicaNet.abonosAcumulados,
    });
  }

  const deudaHoy = calcularDeudaHoy(
    credit,
    infoDeudaBackend?.deudaAFecha,
    infoDeudaBackend?.firmaElectronicaNet,
    infoDeudaBackend?.firmaElectronicaNet?.abonosAcumulados,
  );

  return { costsFeature, deudaHoy, infoDeudaBackend };
}
/**
 * Calcula la deuda hoy aplicando descuento de abonos de firma electrónica
 * según la versión de tarifas del crédito.
 *
 * versionTarifas "2" → aplica descuento si firmaElectronicaNet existe
 * otras versiones    → retorna deudaAFecha sin descuento
 *
 * @param {object} credit               - Objeto del crédito (debe tener versionTarifas)
 * @param {number} deudaAFecha          - Deuda total a la fecha
 * @param {object|null} firmaElectronicaNet  - Objeto de firma electrónica
 * @param {number} abonosAcumulados     - Abonos acumulados de la firma
 * @returns {number}
 */
export function calcularDeudaHoy(
  credit,
  deudaAFecha,
  firmaElectronicaNet,
  abonosAcumulados,
) {
  const version = credit?.versionTarifas;

  switch (version) {
    case "2":
      return firmaElectronicaNet ? deudaAFecha : deudaAFecha;

    default:
      return deudaAFecha;
  }
}

/**
 * Calcula el total de novación iterando costsFeature
 * y aplicando las reglas de descuento por tipo de ítem.
 *
 * @param {Array}  costs                - costsFeature filtrado (sin "Saldo a capital")
 * @param {object|null} firmaElectronicaNet
 * @returns {number}
 */
export function calcularTotalNovacion(costs, firmaElectronicaNet) {
  const abonosAcumulados = firmaElectronicaNet?.abonosAcumulados ?? 0;

  return costs.reduce((total, item) => {
    if (item.item === "DCTO firma electrónica") {
      const descuento = item.value + abonosAcumulados;
      return total - descuento - abonosAcumulados;
    }
    if (item.item === "DCTO recompensa") {
      return total - item.value;
    }
    return total + item.value;
  }, 0);
}

/**
 * Obtiene costsFeature, calcula novación y agrupa abonos por concepto.
 *
 * @param {object} credit
 * @param {object} infoCupo
 * @returns {{ costsNovacion, totalNovacion, conceptosAbonos, totalAbonos }}
 */
/** @returns {Promise<{costsNovacion: Array, totalNovacion: number, conceptosAbonos: Array, totalAbonos: number}>} */
export async function resolverNovacionYAbonos(credit, infoCupo) {
  const versionTarifas = credit?.versionTarifas;

  const responseData =
    versionTarifas === "2"
      ? await HttpRequest.calcularDeudaV2({ credit, infoCupo })
      : await HttpRequest.calcularDeuda({ credit, infoCupo });

  const infoDeudaBackend = responseData.message.deuda;
  const firmaElectronicaNet = infoDeudaBackend?.firmaElectronicaNet ?? null;

  // ── Abonos ────────────────────────────────────────────────
  let conceptosAbonos = [];
  let totalAbonos = 0;

  if (infoDeudaBackend?.abonos) {
    const { conceptos, totalGeneral } = agruparMovimientosPorConcepto(
      infoDeudaBackend.abonos,
    );
    conceptosAbonos = conceptos;
    totalAbonos = totalGeneral;
  }

  // ── Novación ──────────────────────────────────────────────
  const costsNovacion = [
    ...(infoDeudaBackend?.costsFeature ?? []).filter(
      (f) => f.item !== "Saldo a capital",
    ),
    ...(firmaElectronicaNet
      ? [
          {
            item: "DCTO Abono Firma",
            value: firmaElectronicaNet.abonosAcumulados,
          },
        ]
      : []),
  ];

  const totalNovacion = calcularTotalNovacion(
    costsNovacion,
    firmaElectronicaNet,
  );

  return { costsNovacion, totalNovacion, conceptosAbonos, totalAbonos };
}
